import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePresentation } from '@/context/PresentationContext';
import { House, HouseLine, ClipboardText, Stethoscope, PaperPlaneTilt, SignOut, TestTube, ShieldStar, BookOpen, Tag, ListChecks, Buildings, ClockCounterClockwise, Eye, EnvelopeSimple, List, X } from '@phosphor-icons/react';
import TanowMark from '@/components/TanowMark';
import { APP } from '@/constants/testIds';
import CommandPalette from '@/components/CommandPalette';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
const primaryItems = [
    { to: '/dashboard', label: 'Home', icon: House, tid: APP.navDashboard },
    { to: '/households', label: 'Households', icon: HouseLine, tid: APP.navFacilities },
    { to: '/check-ins', label: 'Check-ins', icon: ClipboardText, tid: APP.navSurveys },
    { to: '/sent-check-ins', label: 'Sent check-ins', icon: PaperPlaneTilt, tid: APP.navAssignments },
    { to: '/doctors', label: 'Doctors', icon: Stethoscope, tid: APP.navDoctors },
];

function SidebarBody({ onNavigate }) {
    const { user, isAdmin, isDemoAdmin, isAdminView, impersonating, logout } = useAuth();
    const { on: presentationOn, toggle: togglePresentation } = usePresentation();
    const navigate = useNavigate();

    const coordItems = isDemoAdmin ? [] : primaryItems;

    const handleLogout = async () => {
        await logout();
        onNavigate?.();
        navigate('/login');
    };

    const linkCls = ({ isActive }) =>
        `flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-lg text-sm min-h-[44px] transition-colors duration-100 ${
            isActive ? 'bg-primary text-primary-foreground' : 'text-slate-700 hover:bg-slate-100'
        }`;

    return (
        <div className="flex flex-col h-full">
            <div className="px-6 py-6 border-b border-slate-200">
                <div className="flex items-center gap-2">
                    <TanowMark size={30} />
                    <div>
                        <div className="font-heading text-xl leading-none text-slate-900">Tanow</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Family care, coordinated.</div>
                    </div>
                </div>
            </div>

            <CommandPalette />

            <nav className="flex-1 px-3 py-1 space-y-1 overflow-y-auto">
                {coordItems.map(({ to, label, icon: Icon, tid }) => (
                    <NavLink key={to} to={to} data-testid={tid} className={linkCls} onClick={onNavigate}>
                        <Icon size={18} weight="regular" />
                        <span>{label}</span>
                    </NavLink>
                ))}

                {isAdminView && !impersonating && (
                    <div className="pt-4 mt-4 border-t border-slate-200">
                        <div className="px-3 pb-2 text-[9px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
                            <ShieldStar size={11} weight="duotone" className="text-primary" /> Admin {isDemoAdmin && <span className="text-[8px] uppercase tracking-widest text-slate-400">view only</span>}
                        </div>
                        <NavLink to="/settings/dictionary" data-testid="nav-dictionary" className={linkCls} onClick={onNavigate}>
                            <BookOpen size={18} weight="regular" />
                            <span>Data Dictionary</span>
                        </NavLink>
                        <NavLink to="/settings/tagged-data" data-testid="nav-tagged-data" className={linkCls} onClick={onNavigate}>
                            <Tag size={18} weight="regular" />
                            <span>Tagged Data</span>
                        </NavLink>
                        <NavLink to="/settings/lab-tests" data-testid="nav-lab-tests" className={linkCls} onClick={onNavigate}>
                            <TestTube size={18} weight="regular" />
                            <span>Lab tests</span>
                        </NavLink>
                        {!isDemoAdmin && (
                            <>
                                <NavLink to="/admin/households" data-testid="nav-admin-households" className={linkCls} onClick={onNavigate}>
                                    <Buildings size={18} weight="regular" />
                                    <span>All households</span>
                                </NavLink>
                                <NavLink to="/admin/access-log" data-testid="nav-admin-access-log" className={linkCls} onClick={onNavigate}>
                                    <ClockCounterClockwise size={18} weight="regular" />
                                    <span>Access log</span>
                                </NavLink>
                                <NavLink to="/admin/invites" data-testid="nav-admin-invites" className={linkCls} onClick={onNavigate}>
                                    <EnvelopeSimple size={18} weight="regular" />
                                    <span>Invite a coordinator</span>
                                </NavLink>
                            </>
                        )}
                        <NavLink to="/reminders" data-testid="nav-rules" className={linkCls} onClick={onNavigate}>
                            <ListChecks size={18} weight="regular" />
                            <span>Reminders &amp; Rules</span>
                        </NavLink>
                    </div>
                )}
            </nav>

            <div className="border-t border-slate-200 p-3 space-y-2">
                {isAdmin && !impersonating && (
                    <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm min-h-[44px] border transition-colors duration-100 ${
                            isActive
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'text-primary border-primary/30 hover:bg-primary/5'
                        }`
                    } onClick={onNavigate}>
                        <ShieldStar size={16} weight="duotone" />
                        <span>Admin</span>
                        <span className="ml-auto text-[9px] uppercase tracking-widest bg-primary/10 text-primary px-1.5 py-0.5 rounded">Staff</span>
                    </NavLink>
                )}

                <div data-testid={APP.userMenu} className="flex items-center gap-3 px-2 py-2">
                    {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                            {(user?.name || 'C').slice(0, 1)}
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">{user?.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
                    </div>
                </div>
                <button
                    data-testid="presentation-mode-toggle"
                    onClick={() => { togglePresentation(); }}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm min-h-[44px] transition-colors duration-100 ${
                        presentationOn ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                >
                    <Eye size={16} weight={presentationOn ? 'fill' : 'regular'} />
                    <span className="flex-1 text-left">Presentation mode</span>
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${presentationOn ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-600'}`}>{presentationOn ? 'On' : 'Off'}</span>
                </button>
                <button
                    data-testid={APP.logoutButton}
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm min-h-[44px] text-slate-600 hover:bg-slate-100 transition-colors duration-100"
                >
                    <SignOut size={16} /> Sign out
                </button>
            </div>
        </div>
    );
}

export default function Sidebar() {
    return (
        <aside
            data-testid={APP.sidebar}
            className="hidden md:flex flex-col w-[260px] shrink-0 border-r border-slate-200 bg-white"
        >
            <SidebarBody />
        </aside>
    );
}

export function MobileTopBar() {
    // A separate top bar component; placed inside <main> so it can `sticky top-0`
    // relative to the scrolling main container.
    const [openMobile, setOpenMobile] = useState(false);
    const location = useLocation();
    React.useEffect(() => { setOpenMobile(false); }, [location.pathname]);
    return (
        <>
            <div
                data-testid="mobile-topbar"
                className="md:hidden sticky top-0 z-30 flex items-center justify-between px-3 h-14 bg-white border-b border-slate-200"
            >
                <button
                    data-testid="mobile-menu-open"
                    onClick={() => setOpenMobile(true)}
                    aria-label="Open menu"
                    className="p-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                    <List size={22} weight="regular" />
                </button>
                <div className="flex items-center gap-1.5">
                    <TanowMark size={24} />
                    <span className="font-heading text-lg text-slate-900 leading-none">Tanow</span>
                </div>
                <div className="w-[44px]" />
            </div>
            <Sheet open={openMobile} onOpenChange={setOpenMobile}>
                <SheetContent side="left" className="w-[280px] p-0 flex flex-col" data-testid="mobile-drawer">
                    <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                    <SheetDescription className="sr-only">Tanow navigation</SheetDescription>
                    <button
                        data-testid="mobile-menu-close"
                        onClick={() => setOpenMobile(false)}
                        aria-label="Close menu"
                        className="absolute top-3 right-3 z-10 p-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                        <X size={20} weight="regular" />
                    </button>
                    <SidebarBody onNavigate={() => setOpenMobile(false)} />
                </SheetContent>
            </Sheet>
        </>
    );
}
